// Audio settings
export const RATE = 16000; // 8 kHz sampling
export const TRANSCRIPTION_RATE = RATE; // 8 kHz sampling
export const CHANNELS = 1; // mono
export const FRAMES_PER_BUFFER = 2048; // ScriptProcessor buffer size (not used in AudioWorklet)
export const BYTES_PER_SAMPLE = 2; // 16-bit = 2 bytes
export const STEP_IN_SEC = 0.5; // send 500 milli seconds per WebSocket message

// AudioWorklet always processes 128 samples per quantum
export const AUDIOWORKLET_QUANTUM_SIZE = 128;

// For AudioWorklet: calculate iterations based on 128-sample quanta
export const AUDIOWORKLET_ITERS_PER_SEND = Math.floor(
  (RATE * STEP_IN_SEC) / AUDIOWORKLET_QUANTUM_SIZE,
);

export const ITERS_PER_SEND = Math.floor(
  (RATE * STEP_IN_SEC) / FRAMES_PER_BUFFER,
);

// μ-law encoding function
export const ulawFromPCM = (pcmData) => {
  const ulaw = new Uint8Array(pcmData.length);
  const BIAS = 0x84;
  const CLIP = 32635;

  for (let i = 0; i < pcmData.length; i++) {
    let sample = pcmData[i];

    // Get sign and absolute value
    const sign = (sample >> 8) & 0x80;
    if (sign !== 0) sample = -sample;
    if (sample > CLIP) sample = CLIP;

    // Convert to μ-law
    sample = sample + BIAS;
    let exponent = 7;
    for (let exp = 0x4000; sample < exp && exponent > 0; exp >>= 1, exponent--);

    const mantissa = (sample >> (exponent + 3)) & 0x0f;
    ulaw[i] = ~(sign | (exponent << 4) | mantissa);
  }

  return ulaw;
};

export const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// AudioWorklet processor
export const workletCode = `
class AudioTranscriptionProcessor extends AudioWorkletProcessor {
    constructor() {
        super();
        
        // Initialize buffer and counters
        this.pcmBuffer = [];
        this.frameCount = 0;
        this.bufferCount = 0;
        
        // AudioWorklet processes 128 samples per quantum
        this.quantumSize = 128;
        this.itersPerSend = ${AUDIOWORKLET_ITERS_PER_SEND}; // ~125 iterations for 2 seconds
        this.stepInSec = ${STEP_IN_SEC};
        this.bytesPerSample = ${BYTES_PER_SAMPLE};
        this.sampleRate = ${TRANSCRIPTION_RATE}
        
        console.log('AudioWorklet: itersPerSend =', this.itersPerSend, '(for', this.stepInSec, 'seconds)');
        
        // Set up message handling
        this.port.onmessage = this.handleMessage.bind(this);
        
        // Signal ready state
        this.port.postMessage({ 
            type: 'ready',
            config: {
                quantumSize: this.quantumSize,
                itersPerSend: this.itersPerSend,
                stepInSec: this.stepInSec,
                sampleRate: this.sampleRate
            }
        });
    }

    handleMessage(event) {
        if (event.data.command === 'reset') {
            this.pcmBuffer = [];
            this.frameCount = 0;
            this.bufferCount = 0;
            this.port.postMessage({ type: 'reset-complete' });
        }
    }

    process(inputs, outputs, parameters) {
        // Get input audio data
        const input = inputs[0];
        
        // Process only if we have valid input
        if (!input || !input[0] || input[0].length === 0) {
            return true;
        }
        
        const inputChannel = input[0]; // mono channel
        
        // Convert Float32Array to Int16Array (16-bit PCM)
        const pcmData = new Int16Array(inputChannel.length);
        for (let i = 0; i < inputChannel.length; i++) {
            // Clamp to -1.0 to 1.0 range and convert to 16-bit
            const sample = Math.max(-1, Math.min(1, inputChannel[i]));
            pcmData[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF; ;
        }

        this.bufferCount++;

        // Send individual buffer to main thread for processing
       const clonedBuffer = pcmData.buffer.slice(0);
        this.port.postMessage({
            type: 'audioData',
            data: clonedBuffer,
            bufferCount: this.bufferCount
        }, [clonedBuffer]);

        // Keep processor alive
        return true;
    }
}

// Register the processor
registerProcessor('audio-transcription-processor', AudioTranscriptionProcessor);
`;
